import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiMail, FiPhone, FiMapPin, FiCalendar, FiStar, FiBriefcase, FiShare2 } from 'react-icons/fi';
import { ProfileField, ProfileDataType, Label, profileDataTypeOrder } from '../../types/profile';
import { profileFieldToString, profileDataTypeToString, capitalizeWords } from '../../utils/stringUtils';

interface Props {
  fields: ProfileField[];
  loading?: boolean;
  className?: string;
}

const iconForType = (type: ProfileDataType) => {
  switch (type) {
    case ProfileDataType.EMAIL:
      return <FiMail className="h-5 w-5" />;
    case ProfileDataType.PHONENUMBER:
      return <FiPhone className="h-5 w-5" />;
    case ProfileDataType.ADDRESS:
      return <FiMapPin className="h-5 w-5" />;
    case ProfileDataType.BIRTHDATE:
      return <FiCalendar className="h-5 w-5" />;
    case ProfileDataType.MOMENT:
      return <FiStar className="h-5 w-5" />;
    case ProfileDataType.BUSINESSNAME:
      return <FiBriefcase className="h-5 w-5" />;
    default:
      return <FiMail className="h-5 w-5" />;
  }
};

const getLabelText = (t: any, label?: Label) => {
  switch (label) {
    case Label.WORK:
      return t('page.profile.labels.work');
    case Label.PRIVATE:
      return t('page.profile.labels.private');
    case Label.GENERAL:
      return t('page.profile.labels.general');
    default:
      return t('page.profile.labels.main');
  }
};

export default function ConnectionProfileFields({ fields, loading, className }: Props): JSX.Element | null {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={className}>
        <div className="mt-6 text-sm text-details/80">{t('loading')}</div>
      </div>
    );
  }

  if (!fields || fields.length === 0) {
    return (
      <div className={`mt-6 ${className ?? ''} w-full`}>
        <div className="rounded-lg bg-secondary p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center text-details/70">
            <FiShare2 className="h-6 w-6" />
          </div>
          <div className="mb-1 text-sm font-semibold text-primary">
            {t('page.dashboard.connection.noDataSharedWithYou')}
          </div>
          <div className="text-sm text-details/70">{t('page.dashboard.connection.notSharingWithYou')}</div>
        </div>
      </div>
    );
  }

  // Group by data type using the canonical ordering
  const groups = profileDataTypeOrder
    .map((type) => ({ type, items: fields.filter((f) => f.dataType === type) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className={`mt-6 ${className ?? ''} w-full`}>
      <div className="bg-secondary p-4">
        {groups.map((g) => (
          <div key={g.type} className="mb-4">
            <div className="mb-2 flex items-center gap-3">
              <div className="text-primary">{iconForType(g.type)}</div>
              <div className="text-sm font-semibold text-primary">
                {capitalizeWords(profileDataTypeToString(g.type))}
              </div>
            </div>

            {/* Group by label (General / Work / Private) */}
            {['GENERAL', 'WORK', 'PRIVATE', 'MASKED_EMAIL'].map((lbl) => {
              const labelEnum = lbl as Label;
              const byLabel = g.items.filter((it) => it.label === labelEnum);
              if (byLabel.length === 0) return null;

              return (
                <div
                  key={`${g.type}-${lbl}`}
                  className="mb-2 rounded-lg border border-secondary-200 bg-secondary-200/50 p-2">
                  <div className="mb-1 text-xs text-details/70">{getLabelText(t, labelEnum)}</div>
                  {byLabel.map((f) => (
                    <div
                      key={f.id || `${g.type}-${JSON.stringify(f)}`}
                      className="mb-1 break-words text-sm text-primary">
                      {profileFieldToString(f)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
